/**
 * queue/dataSource.tsx
 * Tracks which data source the app is running on:
 *   • 'memory' — the in-memory store + deterministic engine (default; works with
 *     no backend, resets on reload).
 *   • 'live'   — sk-queue-api is reachable; the real /v1 endpoints back the UI.
 *
 * On mount it probes the API health once. When the API is reachable the app
 * runs 'live' (real /v1 endpoints back the UI); otherwise it falls back to the
 * in-memory store so the app still works standalone.
 *
 * Rollout is per-surface: live wiring lands one screen at a time, so a surface
 * reads live ONLY when mode === 'live' AND it appears in LIVE_SURFACES. Surfaces
 * not yet wired keep reading the in-memory store even while the API is reachable
 * — no half-wired screen shows empty live data. Use isLiveSurface() in a screen
 * to branch, and add the surface key here as it gets wired.
 */

import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { probeQueueApi } from '../services/queue/health';

export type DataSourceMode = 'memory' | 'live';
export type ProbeStatus = 'probing' | 'reachable' | 'unreachable';

/** Surface keys (queue/surfaces.ts) whose live read/write path is wired. */
const LIVE_SURFACES = new Set<string>(['view', 'settings', 'voice', 'chat', 'host']);

interface DataSourceValue {
  /** Active source: 'live' when the API is reachable, else 'memory'. */
  mode: DataSourceMode;
  /** Result of the last API health probe. */
  apiStatus: ProbeStatus;
  apiUrl: string;
  apiDetail?: string;
  reprobe: () => void;
  /** True when this surface should read live (mode live AND surface wired). */
  isLiveSurface: (surfaceKey: string) => boolean;
}

const DataSourceContext = createContext<DataSourceValue | null>(null);

export function DataSourceProvider({ children }: { children: React.ReactNode }) {
  const [apiStatus, setApiStatus] = useState<ProbeStatus>('probing');
  const [apiUrl, setApiUrl] = useState('');
  const [apiDetail, setApiDetail] = useState<string | undefined>();

  const reprobe = useCallback(() => {
    setApiStatus('probing');
    probeQueueApi().then((r) => {
      setApiUrl(r.url);
      setApiDetail(r.detail);
      setApiStatus(r.reachable ? 'reachable' : 'unreachable');
    });
  }, []);

  useEffect(() => { reprobe(); }, [reprobe]);

  // Live when the API is reachable; in-memory otherwise (offline-safe fallback).
  const mode: DataSourceMode = apiStatus === 'reachable' ? 'live' : 'memory';

  const isLiveSurface = useCallback(
    (surfaceKey: string) => mode === 'live' && LIVE_SURFACES.has(surfaceKey),
    [mode],
  );

  return (
    <DataSourceContext.Provider value={{ mode, apiStatus, apiUrl, apiDetail, reprobe, isLiveSurface }}>
      {children}
    </DataSourceContext.Provider>
  );
}

export function useDataSource(): DataSourceValue {
  const ctx = useContext(DataSourceContext);
  if (!ctx) throw new Error('useDataSource must be used within DataSourceProvider');
  return ctx;
}
